import React from 'react';
import Layout from '@/components/layout/Layout';
import PageHeader from '@/components/shared/PageHeader';
import { locations } from '@/data/mock';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Phone, Mail, Clock, ArrowRight } from 'lucide-react';

const LocationsPage = () => {
  return (
    <Layout>
      <PageHeader
        title="National Operations Network"
        subtitle="Strategic delivery hubs coordinating complex infrastructure programs across the United States."
        breadcrumb="Locations"
        backgroundImage="https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1600&q=80"
      />

      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          {locations.map((location) => (
            <Card key={location.id} className="overflow-hidden">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="relative h-64 lg:h-full">
                  <img
                    src={location.image}
                    alt={location.name}
                    className="absolute inset-0 h-full w-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/10" />
                  <div className="absolute bottom-4 left-4 text-white space-y-1">
                    <Badge className="bg-white/20 text-white uppercase tracking-widest">
                      {location.city}
                    </Badge>
                    <h3 className="text-2xl font-semibold">{location.name}</h3>
                    <p className="text-sm text-white/80 max-w-md">{location.regionalFocus}</p>
                  </div>
                </div>

                <CardContent className="p-8 space-y-6">
                  <div className="space-y-2 text-gray-700">
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-widest">
                      Regional leadership
                    </p>
                    <p className="text-lg font-medium">{location.leadership}</p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4 text-sm text-gray-700">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-red-600" />
                        <span className="font-semibold text-gray-900">Address</span>
                      </div>
                      <p>{location.address}</p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-red-600" />
                        <span className="font-semibold text-gray-900">Hours</span>
                      </div>
                      <p>{location.hours}</p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-red-600" />
                        <span className="font-semibold text-gray-900">Phone</span>
                      </div>
                      <a href={`tel:${location.phone}`} className="text-red-600 hover:underline">
                        {location.phone}
                      </a>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-red-600" />
                        <span className="font-semibold text-gray-900">Email</span>
                      </div>
                      <a href={`mailto:${location.email}`} className="text-red-600 hover:underline">
                        {location.email}
                      </a>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-widest mb-3">
                      Core capabilities
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {location.services.map((service) => (
                        <Badge key={service} variant="outline" className="text-xs">
                          {service}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button asChild className="gap-2 flex-1">
                      <a href={location.mapUrl} target="_blank" rel="noopener noreferrer">
                        Launch Directions
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    </Button>
                    <Button asChild variant="outline" className="flex-1">
                      <a href={`mailto:${location.email}`}>Contact Regional Team</a>
                    </Button>
                  </div>
                </CardContent>
              </div>
            </Card>
          ))}
        </div>
      </section>
    </Layout>
  );
};

export default LocationsPage;

